import React, { useState, useEffect } from 'react';
import { Settings, MapPin, Phone, Mail, CheckCircle, Globe, Image as ImageIcon } from 'lucide-react';

/**
 * 昭明机床官网 - 完整内容版
 * 更新：
 * 1. 替换案例：删除江苏南方机电，新增重庆齿轮箱有限责任公司案例
 * 2. 保持 Germany HÖFLER 等品牌翻译规则
 * 3. 修正了英文界面的联系方式
 */
const App = () => {
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    document.title = lang === 'zh' ? "昭明机床 | 升级改造与制造专家" : "ZHAOMING Machine Tool | Retrofit and Manufacturing Experts";
  }, [lang]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => setLang(prev => (prev === 'zh' ? 'en' : 'zh'));

  const content = {
    zh: {
      company: '昭明机床',
      heroTag: '专注成形磨 · 十年匠心',
      heroTitle: '单片成形砂轮磨齿机',
      heroSub: '升级改造与制造专家',
      heroDesc: '致力于高端机床翻新，一次验收成功率 100%。\n从西安到张家港，服务全中国。',
      heroBtnPrimary: '查看成功业绩',
      heroBtnSecondary: '联系我们',
      nav: ['首页', '关于我们', '业务范围', '成功业绩', '联系方式'],
      navIds: ['home', 'about', 'services', 'portfolio', 'contact'],
      aboutTitle: '公司简介',
      aboutTexts: [
        '昭明机床于2015年成立于历史悠久的古城西安市高新区，专注于单片成形砂轮磨齿机的升级改造翻新。',
        '公司核心团队均深耕机床改造行业十余年，经验丰富、责任心强。我们深知成形磨齿机单台价值极高，因此坚持严谨的工作作风与高超的技术水准，确保将完美的机器交付给用户。',
        '2021年，我们在江苏张家港成立了面向东部地区的分公司，并于2022年被评为张家港市领军企业及重点项目。'
      ],
      stats: ['行业改造经验', '一次验收成功率'],
      badgeTitle: '一次验收成功',
      badgeDesc: '我们以极致标准对待每一台交付机型',
      serviceTitle: '业务布局与范围',
      serviceSub: '双核驱动，覆盖全国需求',
      services: [
        {
          title: '西安总部',
          desc: '服务区域：西北、西南地区',
          items: ['磨齿机大修改造业务', '小型精密航空磨齿机制造销售']
        },
        {
          title: '张家港分公司',
          desc: '服务区域：华东、华北、华中等全国范围',
          items: ['全国范围磨齿机大修改造', '风电用大型成型磨齿机制造销售']
        }
      ],
      portfolioTitle: '改造成功业绩',
      portfolioSub: '中国此类机型改造数量最多的公司，以下为部分代表性客户与机型：',
      portfolioWait: '图片加载中',
      footerDesc: '致力于为中国制造业提供最顶尖的磨齿机升级方案，用技术重塑精密价值。',
      footerContact: '联系方式',
      footerOffices: '办事处',
      address: {
        xa: '西安总部：陕西省西安市高新区',
        zjg: '张家港分公司：江苏省张家港市（领军企业）'
      },
      contactInfo: {
        phone: '+86 18066875858',
        email: 'xing.wang@zmmtw.com'
      }
    },
    en: {
      company: 'ZHAOMING Machine Tool Co., Ltd.',
      heroTag: 'Focus on Profile Grinding · Decade of Craftsmanship',
      heroTitle: 'Profile Grinding Machines',
      heroSub: 'Retrofit and Manufacturing Experts',
      heroDesc: 'Dedicated to high-end machine tool refurbishment with 100% acceptance rate.\nServing all of China from Xi\'an to Zhangjiagang.',
      heroBtnPrimary: 'View Portfolio',
      heroBtnSecondary: 'Contact Us',
      nav: ['Home', 'About', 'Services', 'Portfolio', 'Contact'],
      navIds: ['home', 'about', 'services', 'portfolio', 'contact'],
      aboutTitle: 'Company Profile',
      aboutTexts: [
        'Zhaoming Machine Tool Co., Ltd. founded in 2015 in the High-tech Zone of Xi\'an, ZHAOMING specializes in the upgrading, retrofitting, and refurbishment of profile grinding machines.',
        'Our core team has been deeply involved in the machine tool retrofit industry for over a decade. We understand the high value of each machine, maintaining rigorous standards and technical excellence to ensure perfect delivery.',
        'In 2021, we established a branch in Zhangjiagang, Jiangsu to serve Eastern China, which was recognized as a "Leading Enterprise" and key project in 2022.'
      ],
      stats: ['Years of Experience', 'Success Rate'],
      badgeTitle: 'Success on First Inspection',
      badgeDesc: 'We treat every delivery with the highest standards',
      serviceTitle: 'Service Layout & Scope',
      serviceSub: 'Dual-core drive, covering national demand',
      services: [
        {
          title: 'Xi\'an HQ',
          desc: 'Region: Northwest & Southwest China',
          items: ['Grinding machine overhaul & retrofit', 'Manufacturing of precision aerospace grinders']
        },
        {
          title: 'Zhangjiagang Branch',
          desc: 'Region: East, North, Central & National',
          items: ['National-scale grinding machine retrofit', 'Manufacturing of large-scale wind power grinders']
        }
      ],
      portfolioTitle: 'Successful Projects',
      portfolioSub: 'The leading company in China for this type of machine retrofit. Representative clients include:',
      portfolioWait: 'Loading Image',
      footerDesc: 'Dedicated to providing top-tier grinding machine upgrade solutions for China\'s manufacturing industry.',
      footerContact: 'Contact Info',
      footerOffices: 'Offices',
      address: {
        xa: 'HQ: Xi\'an High-tech Zone, Shaanxi',
        zjg: 'Branch: Zhangjiagang, Jiangsu (Leading Enterprise)'
      },
      contactInfo: {
        phone: '+12369908833',
        email: 'yalin.han@zmmtw.com'
      }
    }
  };

  // 案例数据支持多语言
  const portfolios = [
    { 
      zh: { customer: "瑞基机器人有限公司", machine: "德国霍夫勒 P400", type: "成形砂轮磨齿机改造" },
      en: { customer: "Ruiji Robot Co., Ltd.", machine: "Germany HÖFLER P400", type: "Profile Grinder Retrofit" },
      image: "/images/case1.jpg" 
    },
    { 
      zh: { customer: "瑞基机器人有限公司", machine: "瑞士奥力康 OPAL50", type: "成形砂轮磨齿机改造" },
      en: { customer: "Ruiji Robot Co., Ltd.", machine: "Swiss OERLIKON OPAL50", type: "Profile Grinder Retrofit" },
      image: "/images/case2.jpg" 
    },
    { 
      zh: { customer: "欢颜自动化(上海)有限公司", machine: "德国霍夫勒 P400", type: "成形砂轮磨齿机改造" },
      en: { customer: "HuanYan Automation (Shanghai) Co., Ltd.", machine: "Germany HÖFLER P400", type: "Profile Grinder Retrofit" },
      image: "/images/case3.jpg" 
    },
    { 
      zh: { customer: "重庆齿轮箱有限责任公司", machine: "德国 NILES ZP08", type: "成形砂轮磨齿机改造" },
      en: { customer: "Chongqing Gearbox Co., Ltd.", machine: "Germany NILES ZP08", type: "Profile Grinder Retrofit" },
      image: "/images/case8.jpg" 
    },
    { 
      zh: { customer: "河北力特齿轮", machine: "德国 NILES ZE1200", type: "成形砂轮磨齿机大修" },
      en: { customer: "Hebei Lite Gear", machine: "Germany NILES ZE1200", type: "Profile Grinder Overhaul" },
      image: "/images/case5.jpg" 
    },
    { 
      zh: { customer: "杭州杰牌传动", machine: "德国霍夫勒 Helix400", type: "成形砂轮磨齿机大修" },
      en: { customer: "Hangzhou Jie Drive", machine: "Germany HÖFLER Helix400", type: "Profile Grinder Overhaul" },
      image: "/images/case6.jpg" 
    },
  ];

  const t = content[lang];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="app-container">
      <style>{`
        .app-container { font-family: "Microsoft YaHei", "Helvetica Neue", sans-serif; color: #333; line-height: 1.6; }
        nav { position: fixed; width: 100%; top: 0; z-index: 1000; transition: all 0.4s; display: flex; justify-content: center; }
        .nav-inner { width: 90%; max-width: 1200px; display: flex; justify-content: space-between; align-items: center; padding: 15px 0; }
        .nav-scrolled { background: rgba(255,255,255,0.98); box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
        .nav-scrolled .logo, .nav-scrolled .nav-link { color: #1a1a1a !important; }
        
        .hero { 
          height: 100vh; background: #0a0a0a; display: flex; align-items: center; justify-content: center; text-align: center; color: white;
          background-image: linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2000');
          background-size: cover; background-position: center; position: relative;
        }
        .hero-content { max-width: 1000px; padding: 0 20px; z-index: 10; }
        .hero h1 { font-size: clamp(2.2rem, 8vw, 4.2rem); margin-bottom: 20px; font-weight: 900; letter-spacing: -1px; line-height: 1.1; }
        .hero .highlight { color: #3b82f6; }
        .hero p { font-size: clamp(1rem, 3vw, 1.4rem); color: #ddd; margin-bottom: 40px; white-space: pre-line; font-weight: 300; }
        
        .section { padding: 120px 20px; max-width: 1200px; margin: 0 auto; }
        .section-title { font-size: 2.5rem; font-weight: 900; margin-bottom: 50px; position: relative; padding-bottom: 15px; }
        .section-title::after { content: ''; position: absolute; bottom: 0; left: 0; width: 60px; height: 4px; background: #3b82f6; }
        
        .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 30px; }
        
        .card { background: #fff; padding: 45px; border-radius: 24px; border: 1px solid #eee; transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; }
        .card:hover { transform: translateY(-12px); box-shadow: 0 20px 40px rgba(0,0,0,0.05); border-color: #3b82f6; }
        
        .portfolio-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(350px, 1fr)); gap: 30px; }
        .portfolio-item { 
          background: #fff; border-radius: 20px; overflow: hidden; border: 1px solid #e2e8f0; 
          transition: all 0.3s ease; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
        }
        .portfolio-item:hover { transform: scale(1.02); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
        .portfolio-img { width: 100%; height: 220px; background: #f1f5f9; display: flex; align-items: center; justify-content: center; position: relative; }
        .portfolio-img img { width: 100%; height: 100%; object-fit: cover; }
        .portfolio-info { padding: 25px; border-left: 4px solid #3b82f6; }
        .portfolio-info h4 { font-size: 1.15rem; margin-bottom: 8px; color: #1e293b; min-height: 2.8rem; display: flex; align-items: center; }
        .portfolio-info p { color: #64748b; font-size: 0.95rem; line-height: 1.4; }

        .btn-blue { background: #3b82f6; color: white; padding: 16px 40px; border-radius: 50px; border: none; font-weight: bold; cursor: pointer; transition: 0.3s; font-size: 1.1rem; }
        .btn-blue:hover { background: #2563eb; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(59, 130, 246, 0.3); }

        .footer { background: #0f172a; color: #94a3b8; padding: 100px 20px 50px; }
        .footer h4 { color: white; margin-bottom: 25px; font-size: 1.2rem; }
        
        @media (max-width: 768px) {
          .nav-links { display: none; }
          .section { padding: 80px 20px; }
          .hero h1 { font-size: 2.5rem; }
        }
      `}</style>

      {/* 导航栏 */}
      <nav className={scrolled ? 'nav-scrolled' : ''} id="home">
        <div className="nav-inner">
          <div style={{display:'flex', alignItems:'center', gap:'12px', cursor:'pointer'}} onClick={() => scrollToSection('home')}>
            <Settings size={35} color={scrolled ? "#3b82f6" : "#60a5fa"} />
            <span className="logo" style={{fontSize:'26px', fontWeight:'900', color: scrolled ? '#1a1a1a' : 'white', letterSpacing:'-1px'}}>{t.company}</span>
          </div>
          <div className="nav-links" style={{display:'flex', gap:'35px', alignItems:'center'}}>
            {t.nav.map((item, i) => (
              <a 
                key={i} 
                href={`#${t.navIds[i]}`} 
                className="nav-link" 
                onClick={(e) => { e.preventDefault(); scrollToSection(t.navIds[i]); }}
                style={{color: scrolled ? '#1a1a1a' : 'white', textDecoration:'none', fontWeight:'600', fontSize:'15px'}}
              >{item}</a>
            ))}
            <button onClick={toggleLang} style={{background:'transparent', border:'1px solid #888', color: scrolled ? '#333' : 'white', padding:'6px 14px', borderRadius:'8px', cursor:'pointer', fontWeight:'bold'}}>
              {lang === 'zh' ? 'English' : '中文'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero 视觉区 */}
      <section className="hero">
        <div className="hero-content">
          <div style={{color:'#3b82f6', fontWeight:'800', marginBottom:'25px', letterSpacing:'4px', fontSize:'14px'}}>[ {t.heroTag} ]</div>
          <h1>{t.heroTitle}<br/><span className="highlight">{t.heroSub}</span></h1>
          <p>{t.heroDesc}</p>
          <div style={{display:'flex', gap:'20px', justifyContent:'center', flexWrap:'wrap'}}>
            <button className="btn-blue" onClick={() => scrollToSection('portfolio')}>{t.heroBtnPrimary}</button>
            <button 
              onClick={() => scrollToSection('contact')}
              style={{background:'rgba(255,255,255,0.1)', color:'white', border:'1px solid rgba(255,255,255,0.3)', padding:'16px 40px', borderRadius:'50px', cursor:'pointer', fontWeight:'bold'}}
            >{t.heroBtnSecondary}</button>
          </div>
        </div>
      </section>

      {/* 关于我们 */}
      <section className="section" id="about">
        <div className="grid">
          <div>
            <h2 className="section-title">{t.aboutTitle}</h2>
            <div style={{fontSize:'18px', color:'#555', display:'flex', flexDirection:'column', gap:'20px'}}>
              {t.aboutTexts.map((text, idx) => <p key={idx}>{text}</p>)}
            </div>
            <div style={{display:'flex', gap:'40px', marginTop:'50px'}}>
              <div><div style={{fontSize:'42px', fontWeight:'900', color:'#3b82f6'}}>10+</div><div style={{fontSize:'14px', color:'#94a3b8', fontWeight:'bold'}}>{t.stats[0]}</div></div>
              <div><div style={{fontSize:'42px', fontWeight:'900', color:'#10b981'}}>100%</div><div style={{fontSize:'14px', color:'#94a3b8', fontWeight:'bold'}}>{t.stats[1]}</div></div>
            </div>
          </div>
          <div style={{position:'relative', display:'flex', alignItems:'center', justifyContent:'center'}}>
             <div style={{width:'100%', height:'100%', background:'#f1f5f9', borderRadius:'40px', overflow:'hidden', position:'relative'}}>
                <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&q=80&w=1000" alt="Workshop" style={{width:'100%', height:'100%', objectFit:'cover', opacity:0.8}} />
                <div style={{position:'absolute', inset:0, background:'linear-gradient(to top, #3b82f6, transparent)', opacity:0.4}}></div>
             </div>
             <div style={{position:'absolute', bottom:'-20px', right:'-20px', background:'white', padding:'30px', borderRadius:'24px', boxShadow:'0 15px 30px rgba(0,0,0,0.1)', maxWidth:'240px'}}>
                <CheckCircle color="#10b981" size={30} style={{marginBottom:'10px'}} />
                <p style={{fontWeight:'bold', fontSize:'16px'}}>{t.badgeTitle}</p>
                <p style={{fontSize:'12px', color:'#666'}}>{t.badgeDesc}</p>
             </div>
          </div>
        </div>
      </section>

      {/* 业务分布 */}
      <section className="section" id="services" style={{background:'#f8fafc', maxWidth:'none'}}>
        <div style={{maxWidth:'1200px', margin:'0 auto'}}>
          <div style={{textAlign:'center', marginBottom:'70px'}}>
            <h2 className="section-title" style={{margin:'0 auto 20px', display:'inline-block'}}>{t.serviceTitle}</h2>
            <p style={{color:'#64748b', fontSize:'18px'}}>{t.serviceSub}</p>
          </div>
          <div className="grid">
            {t.services.map((service, idx) => (
              <div className="card" key={idx}>
                <div style={{width:'60px', height:'60px', background: idx === 0 ? '#eff6ff' : '#f0fdf4', borderRadius:'16px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'25px'}}>
                  {idx === 0 ? <MapPin color="#3b82f6" size={30} /> : <Globe color="#10b981" size={30} />}
                </div>
                <h3 style={{fontSize:'1.5rem', marginBottom:'15px'}}>{service.title}</h3>
                <p style={{color:'#64748b', marginBottom:'25px'}}>{service.desc}</p>
                <ul style={{listStyle:'none', padding:0, display:'flex', flexDirection:'column', gap:'12px'}}>
                  {service.items.map((item, i) => (
                    <li key={i} style={{display:'flex', alignItems:'center', gap:'10px'}}><CheckCircle size={18} color="#10b981" /> {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 改造业绩 */}
      <section className="section" id="portfolio">
        <h2 className="section-title">{t.portfolioTitle}</h2>
        <p style={{marginBottom:'40px', color:'#64748b', fontSize:'18px'}}>{t.portfolioSub}</p>
        <div className="portfolio-grid">
          {portfolios.map((item, i) => {
            const currentItem = item[lang]; 
            return (
              <div key={i} className="portfolio-item">
                <div className="portfolio-img">
                  <img 
                    src={item.image} 
                    alt={currentItem.machine} 
                    onError={(e) => { 
                      if (!e.target.dataset.tried) {
                        e.target.dataset.tried = "true";
                        e.target.src = `https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&q=60&w=800&sig=${i}`;
                      } else {
                        e.target.style.display='none'; 
                        e.target.nextSibling.style.display='flex'; 
                      }
                    }} 
                  />
                  <div style={{display: 'none', flexDirection:'column', alignItems:'center', gap:'10px'}}>
                    <ImageIcon size={48} color="#cbd5e1" />
                    <span style={{fontSize:'12px', color:'#94a3b8'}}>{t.portfolioWait}</span>
                  </div>
                  <div style={{position:'absolute', top:'10px', right:'10px', background:'rgba(59,130,246,0.9)', color:'white', fontSize:'10px', padding:'4px 8px', borderRadius:'4px'}}>
                    CASE 0{i+1}
                  </div>
                </div>
                <div className="portfolio-info">
                  <h4>{currentItem.customer}</h4>
                  <p style={{fontWeight:'bold', color:'#334155', marginBottom:'4px'}}>{currentItem.machine}</p>
                  <p>{currentItem.type}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 联系方式 */}
      <footer className="footer" id="contact">
        <div style={{maxWidth:'1200px', margin:'0 auto'}}>
          <div className="grid" style={{marginBottom:'80px'}}>
            <div style={{gridColumn:'span 1'}}>
              <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'25px'}}>
                <Settings size={30} color="#3b82f6" />
                <span style={{fontSize:'24px', fontWeight:'900', color:'white'}}>{t.company}</span>
              </div>
              <p style={{lineHeight:'2'}}>{t.footerDesc}</p>
            </div>
            <div>
              <h4>{t.footerContact}</h4>
              <div style={{display:'flex', flexDirection:'column', gap:'15px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}><Phone size={18} color="#3b82f6" /> {t.contactInfo.phone}</div>
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}><Mail size={18} color="#3b82f6" /> {t.contactInfo.email}</div>
              </div>
            </div>
            <div>
              <h4>{t.footerOffices}</h4>
              <p style={{fontSize:'14px', marginBottom:'15px'}}>{t.address.xa}</p>
              <p style={{fontSize:'14px'}}>{t.address.zjg}</p>
            </div>
          </div>
          <div style={{textAlign:'center', borderTop:'1px solid #1e293b', paddingTop:'40px', fontSize:'13px'}}>
            {lang === 'zh' ? `© ${new Date().getFullYear()} 西安昭明机床有限责任公司 版权所有` : `© ${new Date().getFullYear()} ZHAOMING Machine Tool Co., Ltd. All Rights Reserved.`}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
